/**
 * sak-add-on.js — the PDP add-on option (Reviewer packets 164 / 171–172, CD AO1–AO12 + AO9a, Jordan R1–R3).
 * Markup: snippets/product-add-on.liquid (one <sak-add-on> per "add_on" block). Styles: _pdp-add-on.scss.
 *
 * Three jobs:
 *  1. The box: the row toggles (aria-checked). With more than one colour the panel opens in place and a swatch pick
 *     updates the thumbnail, the style name (AO10), the colour name and the price; the first purchasable colour is
 *     preselected and unticking keeps the pick. One colour: nothing expands (AO9) — unless that product has sizes, when
 *     the panel holds only the size row (AO9a). An add-on product with more than one variant gets a size row (AO11):
 *     no size preselected (unless exactly one is available); a picked size carries to the next colour if it is
 *     available there. The add-on adds EXACTLY the picked variant — never a silent first size.
 *  2. Add to Cart: when any box is ticked, the PDP form's OWN payload (id, quantity, properties[...] — read from
 *     FormData, so it is exactly what the form sends today, Purple Dot's fields included) is presented as items[]
 *     plus one item per ticked add-on (`_Added with: <bag>`, `_AddOn: <block label>`), by rewriting the entries in the form's `formdata`
 *     event (one-shot) when Broadcast's CartItems builds its FormData inside the click — no control is disabled
 *     or appended, so the DOM never changes (F2, packet 182: the external input[name="id"] is Tangiblee's observed
 *     node). Broadcast's ProductForm → theme:cart:add → CartItems.addToCart then posts the ONE request, opens the
 *     drawer, fires the events and prints any error, unchanged. With no box ticked nothing here runs and the
 *     request is today's. (Shopify ignores items[] when a plain `id` is present — probed 2026-10-21 — hence the
 *     delete, not an add.)
 *     Indices are numeric and the bag goes last, for Purple Dot's appended pre-order properties (see below).
 *     AD2 (packets 253/254): when the block carries a discount (data-discount, emitted only for a discount_type other
 *     than None) each add-on item also gets `_sak_add_on: <bag handle>`; the sak-discounts app's discount function
 *     discounts only lines with that property. With None the payload is unchanged.
 *     A ticked box that needs a size and has none BLOCKS the click (AO11): nothing is added, not even the bag; the
 *     box scrolls into view (window.scrollTo) and "Select a size" shows with role="alert". A pick or an untick clears it.
 *  3. A bag colour swap (sibling-color-swapper.js) leaves this box in place (outside its zones), so the tick and
 *     the colour carry (R2); on color-swap:complete the box hides when the new colour is outside show_on_collection
 *     or sold out (the server-rendered data-show-on map), and the add-on's "_Added with" follows the new title.
 */
(() => {
  if (window.__sakAddOn) return;
  window.__sakAddOn = true;

  const TAG = 'sak-add-on';
  const PDP_BUTTONS = '.product__block--buttons';
  const NOT_PDP = '[data-quick-add-holder], [data-quick-add-modal], .cart-drawer, .product-upsell, .upsell-products, [data-cart-bar]';

  class SakAddOn extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.row = this.querySelector('[data-add-on-row]');
      this.panel = this.querySelector('[data-add-on-panel]');
      this.thumb = this.querySelector('[data-add-on-thumb]');
      this.priceEl = this.querySelector('[data-add-on-price]');
      this.wasEl = this.querySelector('[data-add-on-was]');
      this.colorEl = this.querySelector('[data-add-on-color]');
      this.styleEl = this.querySelector('[data-add-on-style]');
      this.sizeRow = this.querySelector('[data-add-on-size]');
      this.sizeValueEl = this.querySelector('[data-add-on-size-value]');
      this.sizesEl = this.querySelector('[data-add-on-sizes]');
      this.errorEl = this.querySelector('[data-add-on-error]');
      this.swatches = [...this.querySelectorAll('[data-add-on-swatch]')];
      this.expands = this.dataset.expands === 'true';
      this.on = false;
      this.selected = null;
      this.variants = [];
      this.size = null; // the picked variant {id, size, available, price, was}, or null
      this.handle = this.dataset.handle || '';
      this.bagTitle = this.dataset.bagTitle || '';
      this.label = this.dataset.addOnLabel || '';
      // AD2 (packets 253/254): the block's discount_type when it is not None (Liquid emits data-discount only then)
      this.discount = this.dataset.discount || '';
      try { this.showOn = JSON.parse(this.dataset.showOn || '{}'); } catch (_) { this.showOn = {}; }
      const total = parseInt(this.dataset.showOnTotal || '0', 10);
      const mapped = Object.keys(this.showOn).length;
      // A show_on collection over 50 products renders a partial map (Liquid's loop cap): unknown handles stay visible.
      this.showOnComplete = mapped > 0 && mapped >= total;
      this.showOnUsed = mapped > 0;

      const first = this.swatches.find((s) => !s.disabled);
      if (first) this.select(first);
      if (!this.row) return;
      this.row.addEventListener('click', () => this.toggle());
      this.swatches.forEach((s) => s.addEventListener('click', () => { if (!s.disabled) this.select(s); }));
    }

    /** Ticked, visible and with a purchasable colour: the add this box contributes to Add to Cart. */
    get checked() {
      return this.on && !this.hidden && !!this.selected && !this.selected.disabled;
    }

    /** The selected product needs a size (more than one variant). */
    get needsSize() {
      return this.variants.length > 1;
    }

    /** Ticked but no size picked (AO11): Add to Cart is blocked. */
    get blocked() {
      return this.checked && this.needsSize && !this.size;
    }

    /** The exact variant to add: the picked size's id, or the single variant's id. Null while a size is missing. */
    get variantId() {
      if (this.needsSize) return this.size ? String(this.size.id) : null;
      if (this.variants.length === 1) return String(this.variants[0].id);
      return (this.selected && this.selected.dataset.variantId) || null;
    }

    toggle(force) {
      this.on = typeof force === 'boolean' ? force : !this.on;
      this.row.setAttribute('aria-checked', this.on ? 'true' : 'false');
      this.classList.toggle('is-open', this.on);
      if (!this.on) this.setError(false);
      if (!this.panel) return; // AO9: one colour, one variant — only the box changes
      // The closed panel is laid out (the grid-rows animation) but must not be reachable: inert + aria-hidden.
      this.panel.inert = !this.on;
      this.panel.setAttribute('aria-hidden', this.on ? 'false' : 'true');
    }

    select(swatch) {
      this.selected = swatch;
      this.swatches.forEach((s) => s.setAttribute('aria-checked', s === swatch ? 'true' : 'false'));
      const d = swatch.dataset;
      if (this.thumb && d.thumb && this.thumb.getAttribute('src') !== d.thumb) this.thumb.src = d.thumb;
      if (this.colorEl) this.colorEl.textContent = d.color || '';
      if (this.styleEl) this.styleEl.textContent = d.style || '';
      let variants = [];
      if (d.variants) { try { variants = JSON.parse(d.variants); } catch (_) { variants = []; } }
      this.variants = Array.isArray(variants) ? variants : [];
      this.renderSizes();
      this.setPrice(this.size || { price: d.price || '', was: d.was || '' });
    }

    setPrice({ price, was }) {
      if (this.priceEl) {
        this.priceEl.textContent = price || '';
        this.priceEl.classList.toggle('is-sale', !!was);
      }
      if (this.wasEl) {
        this.wasEl.textContent = was || '';
        this.wasEl.hidden = !was;
      }
    }

    /** AO11: the size row for the selected product; a previous pick carries over when that size is available here. */
    renderSizes() {
      const previous = this.size ? this.size.size : null;
      this.size = null;
      if (!this.sizeRow || !this.sizesEl) return;
      if (!this.needsSize) {
        this.sizeRow.hidden = true;
        this.sizesEl.replaceChildren();
        this.setError(false);
        return;
      }
      const buttons = this.variants.map((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', 'false');
        b.className = 'sak-add-on__size-btn' + (v.available ? '' : ' is-sold-out');
        b.textContent = v.size;
        b.dataset.addOnSizeBtn = '';
        if (!v.available) {
          b.disabled = true;
          b.setAttribute('aria-label', `${v.size}, sold out`);
        }
        b.addEventListener('click', () => { if (v.available) this.pickSize(v, b); });
        return b;
      });
      this.sizesEl.replaceChildren(...buttons);
      this.sizeRow.hidden = false;
      const available = this.variants.filter((v) => v.available);
      const carried = previous !== null ? available.find((v) => v.size === previous) : null;
      const only = available.length === 1 ? available[0] : null;
      const pick = carried || only;
      if (pick) this.pickSize(pick, buttons[this.variants.indexOf(pick)]);
      else if (this.sizeValueEl) this.sizeValueEl.textContent = '';
    }

    pickSize(variant, button) {
      this.size = variant;
      this.sizesEl.querySelectorAll('[data-add-on-size-btn]').forEach((b) => b.setAttribute('aria-checked', b === button ? 'true' : 'false'));
      if (this.sizeValueEl) this.sizeValueEl.textContent = variant.size;
      this.setPrice(variant);
      this.setError(false);
    }

    setError(show) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !show;
      this.classList.toggle('has-error', !!show);
    }

    /** After a bag colour swap: stay for a colour that carries the offer, go for one that doesn't (R2). */
    onSwap(handle, productData) {
      if (!handle) return;
      let visible = true;
      if (this.showOnUsed) {
        if (Object.prototype.hasOwnProperty.call(this.showOn, handle)) visible = this.showOn[handle] === 1;
        else visible = !this.showOnComplete;
      }
      if (visible && productData && productData.available === false) {
        const tags = Array.isArray(productData.tags) ? productData.tags : [];
        if (!tags.includes('_preorder') && !Object.prototype.hasOwnProperty.call(this.showOn, handle)) visible = false;
      }
      this.hidden = !visible;
      this.handle = handle;
      if (productData && typeof productData.title === 'string') {
        this.bagTitle = productData.title.split(' - ')[0].trim() || this.bagTitle;
      }
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, SakAddOn);

  function pdpForm(button) {
    if (button.closest(NOT_PDP)) return null;
    if (!button.closest(PDP_BUTTONS)) return null;
    const form = button.form || button.closest('form');
    if (!form || !form.hasAttribute('data-product-form')) return null;
    return form;
  }

  /** AO11: scroll the box into view with window.scrollTo (not scrollIntoView), under the sticky header. */
  function revealBox(box) {
    let header = 0;
    try { header = (window.theme && window.theme.readHeights && window.theme.readHeights().stickyHeaderHeight) || 0; } catch (_) { header = 0; }
    const top = box.getBoundingClientRect().top + window.scrollY - header - 16;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), left: 0, behavior: reduce ? 'auto' : 'smooth' });
  }

  // Capture phase on document: runs before Broadcast's ProductForm click handler on the button, which dispatches
  // theme:cart:add synchronously; CartItems builds FormData(form) inside that dispatch, i.e. inside this click.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-add-to-cart]');
    if (!button || button.disabled) return;
    const form = pdpForm(button);
    if (!form) return;
    const boxes = [...document.querySelectorAll(TAG)];
    const blocked = boxes.filter((a) => a.blocked);
    if (blocked.length) {
      // Nothing is added, not even the bag: the click never reaches Broadcast's handler on the button.
      event.preventDefault();
      event.stopImmediatePropagation();
      blocked.forEach((a) => a.setError(true));
      revealBox(blocked[0]);
      return;
    }
    const ticked = boxes.filter((a) => a.checked && a.variantId);
    if (!ticked.length) return;

    const fd = new FormData(form);
    let bagId = null;
    let bagQty = '1';
    const props = new Map();
    for (const [key, value] of fd.entries()) {
      if (key === 'id') bagId = value;
      else if (key === 'quantity') bagQty = value;
      else if (key.startsWith('properties[') && key.endsWith(']')) props.set(key.slice(11, -1), value);
    }
    if (!bagId) return;

    // F2 (packet 182): the payload is rewritten in the form's `formdata` event — the one `new FormData(form)` that
    // Broadcast's CartItems builds inside this click fires it, and its event.formData is mutable — so NO control is
    // disabled or appended: nothing in the DOM changes, and in particular the external input[name="id"] that Tangiblee
    // observes for attribute changes is never touched (CLAUDE.md, packet 169). M1 (packet 201): EVERY `formdata` event
    // in this click is rewritten, not just the first — Meta Pixel's click handler builds FormData(form) twice before
    // Broadcast does in WebKit, and a one-shot listener spent itself on Meta's copy (Safari added only the bag). Each
    // event carries its own FormData built from the untouched form, so the rewrite is per object and idempotent. The
    // listener is removed in the next task, after Broadcast's read.
    // Numeric indices, add-ons first, the bag LAST: Purple Dot's fetch wrapper appends its pre-order properties to
    // the end of the body as items[<index of the bag>][properties][…], and Shopify reads an item's fields only while
    // they are contiguous — properties that arrive after a later item's fields are dropped (probed 2026-10-21).
    const onData = (ev) => {
      const data = ev.formData;
      for (const key of [...new Set(data.keys())]) {
        if (key === 'id' || key === 'quantity' || (key.startsWith('properties[') && key.endsWith(']'))) data.delete(key);
      }
      let index = 0;
      ticked.forEach((addOn) => {
        data.append(`items[${index}][id]`, addOn.variantId);
        data.append(`items[${index}][quantity]`, '1');
        data.append(`items[${index}][properties][_Added with]`, addOn.bagTitle);
        // J1 (packet 191): the block's label, hidden (the `_` prefix), for Flow and Matrixify to find add-on lines
        if (addOn.label) data.append(`items[${index}][properties][_AddOn]`, addOn.label);
        // AD2 (packets 253/254): with a discount on the block the line carries `_sak_add_on` = the bag's handle. The
        // sak-discounts function discounts only lines with this property, so the same tassel from its own PDP pays
        // full price. With None (no data-discount) the payload is today's bytes.
        if (addOn.discount) data.append(`items[${index}][properties][_sak_add_on]`, addOn.handle);
        index += 1;
      });
      data.append(`items[${index}][id]`, bagId);
      data.append(`items[${index}][quantity]`, bagQty);
      props.forEach((value, key) => data.append(`items[${index}][properties][${key}]`, value));
    };
    form.addEventListener('formdata', onData);
    setTimeout(() => form.removeEventListener('formdata', onData), 0);
  }, true);

  document.addEventListener('color-swap:complete', ({ detail }) => {
    setTimeout(() => {
      let handle = '';
      try {
        const path = new URL(detail.url, location.href).pathname;
        const m = path.match(/\/products\/([^/?#]+)/);
        handle = m ? decodeURIComponent(m[1]) : '';
      } catch (_) { /* keep '' */ }
      document.querySelectorAll(TAG).forEach((addOn) => addOn.onSwap(handle, detail.productData));
    }, 0);
  });
})();
